/**
 * Respuesta de error estandarizada desde el backend
 *
 * @remarks
 * Esta interfaz representa la estructura que el backend debe retornar
 * cuando ocurre un error. El frontend NO genera códigos de error localmente.
 *
 * Ejemplo de respuesta del backend:
 * {
 *   "error": {
 *     "code": "ER-NET-900",
 *     "message": "Error al cargar países",
 *     "details": "El servidor no respondió a tiempo",
 *     "timestamp": "2026-01-07T15:30:45Z",
 *     "interactionId": "a1b2c3d4-5678-90ab-cdef-1234567890ab"
 *   }
 * }
 */
export interface BackendErrorResponse {
  /** Código de error del backend (ER-XXX-YYY) */
  code: string;

  /** Mensaje principal para mostrar al usuario */
  message: string;

  /** Detalles técnicos o instrucciones adicionales */
  details?: string;

  /** Timestamp ISO 8601 del error */
  timestamp: string;

  /** ID de interacción para trazabilidad */
  interactionId: string;
}

/**
 * Interfaz para errores HTTP procesados en el frontend
 *
 * @remarks
 * Extiende BackendErrorResponse con información adicional del HttpErrorResponse
 */
export interface StandardHttpError extends BackendErrorResponse {
  /** Código de estado HTTP original */
  status: number;

  /** URL del endpoint que generó el error */
  url?: string;

  /** Error original de Angular HttpClient (para debugging) */
  originalError?: unknown;
}

/**
 * Type guard para validar BackendErrorResponse
 */
export function isBackendErrorResponse(obj: unknown): obj is BackendErrorResponse {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'code' in obj &&
    'message' in obj &&
    'timestamp' in obj &&
    'interactionId' in obj
  );
}

/**
 * Type guard para validar StandardHttpError
 */
export function isStandardHttpError(obj: unknown): obj is StandardHttpError {
  return (
    isBackendErrorResponse(obj) &&
    'status' in obj
  );
}
